import { Injectable } from '@angular/core';

import { STAT_VALUE_FORMAT } from '@containers/reports/dashboard/multi-chart/models/statKey.model';
import { PreferencesService } from '@shared/services';

type StrictNumberFormatOptions = Omit<Intl.NumberFormatOptions, 'style' | 'currencySign'> & {
    // Based on https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
    style?: 'decimal' | 'currency' | 'percent' | 'unit';
    currencySign?: 'standard' | 'accounting';
};

@Injectable({
    providedIn: 'root',
})
export class FormatterService {
    private MAX_LONG_FORMAT_VALUE = 100000;

    constructor(private preferencesService: PreferencesService) {}

    public convertToShortInternational(value: number, format: STAT_VALUE_FORMAT, precision = null) {
        if (format === STAT_VALUE_FORMAT.CURRENCY || format === STAT_VALUE_FORMAT.AMOUNT) {
            return this.convertToShortInternationalCurrencySystem(value);
        }
        if (format === STAT_VALUE_FORMAT.DECIMAL || format === STAT_VALUE_FORMAT.NUMBER) {
            return this.convertToShortInternationalNumberSystem(value, precision);
        }
        if (format === STAT_VALUE_FORMAT.FLOAT) {
            return this.convertToShortInternationalFloatSystem(value, precision);
        }
        return value;
    }

    public convertToFullValueFormat(value: number, format: STAT_VALUE_FORMAT, precision = null) {
        if (format === STAT_VALUE_FORMAT.CURRENCY || format === STAT_VALUE_FORMAT.AMOUNT) {
            return this.preferencesService.currencyFormatterForAgGrid({ value });
        }
        if (format === STAT_VALUE_FORMAT.DECIMAL || format === STAT_VALUE_FORMAT.NUMBER) {
            return this.preferencesService.numberFormatterForAgGrid({ value });
        }
        if (format === STAT_VALUE_FORMAT.FLOAT) {
            return this.preferencesService.floatFormatterForAgGrid(value, precision);
        }
        if (format === STAT_VALUE_FORMAT.PERCENT) {
            value *= 100;
            return this.preferencesService.percentsFormatterForAgGrid({ value }, precision);
        }
        return value;
    }

    public convertToShortInternationalNumberSystem(value: number, precision = null): string {
        if (value < this.MAX_LONG_FORMAT_VALUE) {
            return this.preferencesService.formatToNumber(value, precision).toString();
        }
        const options: StrictNumberFormatOptions = {
            notation: 'compact',
            compactDisplay: 'short',
            style: 'decimal',
        };
        return Intl.NumberFormat(this.preferencesService.timeLocale, options).format(value);
    }

    public convertToShortInternationalFloatSystem(value: number, precision = null): string {
        if (value < this.MAX_LONG_FORMAT_VALUE) {
            return this.preferencesService.formatToFloatNumber(value, precision).toString();
        }
        const options: StrictNumberFormatOptions = {
            notation: 'compact',
            compactDisplay: 'short',
            style: 'decimal',
        };
        return Intl.NumberFormat(this.preferencesService.timeLocale, options).format(value);
    }

    public convertToShortInternationalCurrencySystem(value: number): string {
        if (value < this.MAX_LONG_FORMAT_VALUE) {
            return this.preferencesService.formatToCurrency(value).toString();
        }
        const companyPreferences = this.preferencesService.currencyAndNumbersPreferences;
        const options: StrictNumberFormatOptions = {
            notation: 'compact',
            compactDisplay: 'short',
            style: 'currency',
            currency: this.preferencesService.currencyAndNumbersPreferences.currency,
            minimumFractionDigits: companyPreferences.precision,
            maximumFractionDigits: companyPreferences.precision,
            currencySign: 'accounting',
        };
        return Intl.NumberFormat(this.preferencesService.timeLocale, options)
            .format(value)
            .replace('accounting', this.preferencesService.currencyAndNumbersPreferences.symbol);
    }

    public convertToInternationalCurrencySystem(value: number, type = 'grid'): string {
        return this.preferencesService.currencyFormatterForAgGrid(value, type);
    }

    public convertToInternationalNumberSystem(value: number, type = 'grid'): string | number {
        return this.preferencesService.numberFormatterForAgGrid(value, null, type);
    }

    public convertToInternationalFloatSystem(value: number, precision = null, type = 'grid'): string | number {
        return this.preferencesService.floatFormatterForAgGrid(value, precision, false, type);
    }

    public tooltipRenderer(values, yKeys, xKeys, yNames, colors, bgColor): string {
        const bodyTemplates = yKeys.map((key, index) => {
            if (yNames[index] && (values[key] || values[key] === 0)) {
                return `
             <div style="display: flex; place-items: center; gap: 10px; padding: 5px 0;">
                    <div style="width: 12px; height: 12px; border-radius: 100%; background: ${colors[index]}"></div>
                    <div style="display: flex; flex-direction: column">
                        <span style="font-family: 'Source Sans Pro', Arial, Helvetica, sans-serif; color: #333; font-weight: 700; text-align: start;">${yNames[index]}</span>
                        <span style="font-family: 'Source Sans Pro', Arial, Helvetica, sans-serif; color: #333; font-weight: 400; text-align: start;">${values[key]}</span>
                    </div>
                </div>
    `;
            }
        });
        const headerTemplates = xKeys.map((key) => {
            if (values[key] !== null && values[key] !== undefined) {
                return ` <span>${values[key]}</span>`;
            }
        });
        return `
      <div class="ag-chart-tooltip-title" style="background-color: ${bgColor}; text-align: center">
        ${headerTemplates.join(', ')}
      </div>
      <div class="ag-chart-tooltip-content">
          ${bodyTemplates.join(' ')}
      </div>`;
    }
}
